import React, { useCallback, useEffect, useRef, useState } from 'react';
import './draggable-entity-card.css';
import {
	getCanvasEntityMainValueOutputDOMId,
	getCardColorCSSVariable,
	highlightElementIndefinetly,
	highlightElementSuccess,
	removeHighlightElementIndefinetly,
	removeHighlightElementSuccess,
	resolveEntityColorClass,
	resolveMainValueBackgroundCSSVariable
} from '../../services/editor/ui';
import { Canvas } from '../../lib/canvas';
import {
  entityDisabledReasonToShortReadableError,
	EntityType,
	PassThroughCallableEntityWithOutputsState,
	VARIABLE_TYPES
} from '@elyx-code/project-logic-tree';
import { ConnectionNodeType, EditorService } from '../../services/editor';
import {
	findReferencesToSelfInProject,
	CanvasEntityDisabledReason
} from '@elyx-code/project-logic-tree';
import { CanvasEntityElement } from '../../services/editor/CanvasElement';
import DynamicValue from '../dynamic-value/DynamicValue';
import DataTypeIcon from './DataTypeIcon';
import { Logger } from '@elyx-code/common-ts-utils';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';

interface IMainValueOutputProps {
	entity: PassThroughCallableEntityWithOutputsState;
	canvasElement: CanvasEntityElement;
	project: EditorService;
	parentDisabledReasons: CanvasEntityDisabledReason[];
}

export default function MainValueOutput(props: IMainValueOutputProps) {
	const rootElementRef = useRef<HTMLLIElement>(null);
	const [isConnected, setIsReturnConnected] = useState<boolean>(false);

	const resolveTooltip = () => {
		return (
			<h3 style={{ marginTop: 0, marginBottom: 5 }}>
				Click and drag to connect to another value
			</h3>
		);
	};

	const memoizedCanvasSocketTooltipBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				id={
					'canvas-main-value-output-socket-tooltip-' +
					props.entity.id +
					'--popup--body'
				}
				containerClasses={['tooltip', 'tooltip--large-font']}
			>
				{resolveTooltip()}
			</CanvasPopupBaseComponent>
		),
		[]
	);

	const canvasSocketTooltip = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-main-value-output-socket-tooltip-' +
				props.entity.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-main-value-output-socket-tooltip-' +
				props.entity.id +
				'--popup',
			placement: 'right',
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-main-value-output-socket-tooltip-' +
				props.entity.id +
				'--popup--body'
		},
		memoizedCanvasSocketTooltipBody,
		[]
	).setupHover({
		openDelay: 600,
		closeDelay: 400
	});

	useEffect(() => {
		const entity = props.entity;

		// --- Initial Logic (runs on mount and when dependencies change) ---
		setIsReturnConnected(
			!!entity?.writesValues.length ||
				!!findReferencesToSelfInProject(entity)?.length
		);

		const handleEntityUpdate = () => {
			setIsReturnConnected(
				!!entity?.writesValues.length ||
					!!findReferencesToSelfInProject(entity)?.length
			);
		};

		const handleConnectionStart = (payload: any) => {
			if (
				!!payload.compatible?.[entity.id] &&
				[ConnectionNodeType.PropertyIn].includes(payload.nodeType)
			) {
				if (rootElementRef.current) {
					highlightElementIndefinetly(rootElementRef.current);
				}
			}
		};

		const handleConnectionEnd = () => {
			if (rootElementRef.current) {
				removeHighlightElementIndefinetly(rootElementRef.current);
				removeHighlightElementSuccess(rootElementRef.current);
			}
		};

		props.project.on(props.entity.id, handleEntityUpdate);
		props.project.on('canvas-connection-start', handleConnectionStart);
		props.project.on('canvas-connection-end', handleConnectionEnd);

		return () => {
			props.project.off(props.entity.id, handleEntityUpdate);
			props.project.off('canvas-connection-start', handleConnectionStart);
			props.project.off('canvas-connection-end', handleConnectionEnd);

			// Also clean up any lingering highlights just in case
			if (rootElementRef.current) {
				removeHighlightElementIndefinetly(rootElementRef.current);
				removeHighlightElementSuccess(rootElementRef.current);
			}
		};
	}, [
		props.project,
		props.entity, // entity is derived from props, so props.entity is the dependency
		rootElementRef,
		setIsReturnConnected,
		findReferencesToSelfInProject,
		highlightElementIndefinetly,
		removeHighlightElementIndefinetly,
		removeHighlightElementSuccess
	]);

	const entity = props.entity;

	const disabled =
		!!props.parentDisabledReasons.length ||
		(!!props.project.onGoingConnectionNodeType &&
			props.project.onGoingConnectionNodeType !==
				ConnectionNodeType.PropertyIn);

	// When the entity is part of a variable declaration,
	// which has one of their internal calls being executed,
	// we can't connect any values to it
	const dontShow =
		(entity.type === EntityType.VariableDeclaration ||
			entity.type === EntityType.VariableInstance) &&
		entity.internalCalls.some((call) => !!call.calledBy.length);

	if (dontShow) {
		return null;
	}

	let altOutputName: string | null = null;

	if (
		!!(
			entity.type === EntityType.Operation ||
			entity.type === EntityType.InternalCall
		) &&
		entity.declaration?.type === EntityType.ActionDescriptor &&
		!!entity.declaration.outputName
	) {
		altOutputName = entity.declaration.outputName;
	}

	const canBeObject = entity.getDataType(null)?.canBeObject();

	return (
		<li
			ref={rootElementRef}
			className={[
				'canvas-draggable-card__property-row',
				'canvas-draggable-card__main-property',
				'canvas-draggable-card__main-value-property',
				'canvas-draggable-card__main-output-property',
				'canvas-draggable-card__main-output-property__top-margin',
				...(disabled ? ['disabled'] : [])
			].join(' ')}
			id={getCanvasEntityMainValueOutputDOMId(entity)}
			style={{
				backgroundColor: resolveMainValueBackgroundCSSVariable(entity)
			}}
			{...{
				[Canvas.OBJ_ATTR_NAME]:
					getCanvasEntityMainValueOutputDOMId(entity)
			}}
			onMouseOver={(e: any) => {
				if (!!props.project.canvas?.activeConnectionDrawing) {
					if (
						props.project.onGoingConnectionCompatibleEntities?.[
							props.entity.id
						] &&
						[ConnectionNodeType.PropertyIn].includes(
							props.project.onGoingConnectionNodeType
						)
					) {
						if (
							props.project.onGoingConnectionTarget &&
							[ConnectionNodeType.PropertyIn].includes(
								props.project.onGoingConnectionNodeType
							)
						) {
							props.project.onGoingConnectionSource =
								props.entity;
							props.project.onGoingConnectionSourceId =
								getCanvasEntityMainValueOutputDOMId(entity);
						}

						props.project.canvas?.activeConnectionDrawing.snap(
							getCanvasEntityMainValueOutputDOMId(entity)
						);

						removeHighlightElementIndefinetly(
							rootElementRef.current
						);
						highlightElementSuccess(rootElementRef.current);
					} else if (
						!!props.project.onGoingConnectionIncompatibleEntities?.[
							props.entity.id
						]
					) {
						// Read the disabled reasons and add a tooltip to the mouse
						const reasonsMessage =
							props.project.onGoingConnectionIncompatibleEntities?.[
								props.entity.id
							].reasons
								.map((reason) =>
									entityDisabledReasonToShortReadableError(
										reason
									)
								)
								.join('.\n');

						props.project.canvas?.showTooltip(
							'connection-disabled-reasons-main-value-output-' +
								props.entity.id,
							reasonsMessage
						);
						if (props.project.canvas) {
							props.project.canvas.tooltip.style.fontSize =
								'28px';
							props.project.canvas.canvasElement.style.cursor =
								'not-allowed';
						}
					}
				}
			}}
			onMouseLeave={(e: any) => {
				if (
					props.project.canvas?.activeUserSetTooltipId ===
					'connection-disabled-reasons-main-value-output-' +
						props.entity.id
				) {
					props.project.canvas?.hideTooltip(
						'connection-disabled-reasons-main-value-output-' +
							props.entity.id
					);
				}

				if (
					!!props.project.canvas?.activeConnectionDrawing &&
					props.project.onGoingConnectionCompatibleEntities?.[
						entity.id
					] &&
					[ConnectionNodeType.PropertyIn].includes(
						props.project.onGoingConnectionNodeType
					)
				) {
					removeHighlightElementIndefinetly(rootElementRef.current);
					highlightElementIndefinetly(rootElementRef.current);

					if (
						props.project.canvas?.activeConnectionDrawing
							?.snappedTo ===
						getCanvasEntityMainValueOutputDOMId(entity)
					) {
						props.project.canvas?.activeConnectionDrawing?.unSnap();
					}

					if (
						props.project.onGoingConnectionSource ===
							props.entity &&
						props.project.onGoingConnectionSourceId ===
							getCanvasEntityMainValueOutputDOMId(entity) &&
						[ConnectionNodeType.PropertyIn].includes(
							props.project.onGoingConnectionNodeType
						)
					) {
						Logger.log('Removing itself as source');
						props.project.onGoingConnectionSource = null;
						props.project.onGoingConnectionSourceId = null;
					}
				}
			}}
		>
			<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
				<br />
			</span>
			<div className="canvas-draggable-card__property__right-block">
				<div className="canvas-draggable-card__property__labels-group">
					<span className="canvas-draggable-card__output-property__name">
						{[
							EntityType.InternalCall,
							EntityType.FunctionCall,
							EntityType.Operation,
							EntityType.Search,
							EntityType.Loop
						].includes(entity.type) ? (
							altOutputName || (
								<>
									<b>READ</b> full output value
								</>
							)
						) : (
							<>
								<b>READ</b>
								{canBeObject ? ' full value' : ' value'}
							</>
						)}
					</span>
					<p className="canvas-draggable-card__property__type">
						{!VARIABLE_TYPES.includes(entity.type) ? (
							<DynamicValue
								valueOwner={entity}
								project={props.project}
								parentCanvasObject={
									props.canvasElement.draggableObject
								}
								parentDisabledReasons={[]}
							/>
						) : canBeObject ? (
							'With all the properties'
						) : (
							'Retrieve latest value'
						)}
					</p>
				</div>
				{VARIABLE_TYPES.includes(entity.type) ? (
					<i className="fas fa-arrow-right-from-bracket" />
				) : (
					<DataTypeIcon
						entity={entity}
						project={props.project}
						parentCanvasObject={
							props.canvasElement?.draggableObject
						}
					/>
				)}
			</div>
			<div
				id={`${getCanvasEntityMainValueOutputDOMId(entity)}--connected-right-node`}
				{...{
					[Canvas.OBJ_ATTR_NAME]: `${getCanvasEntityMainValueOutputDOMId(
						entity
					)}--connected-right-node`
				}}
				className={[
					'canvas__small-node',
					'canvas__small-node--position',
					// Removed 'invisible' so the resting state stays visible
					...(isConnected
						? ['canvas-property__node__connected']
						: [
								'canvas__medium-node',
								resolveEntityColorClass(props.entity.type)
							])
				].join(' ')}
				style={{
					position: 'absolute',
					top: '50%',
					right: '-9px',
					transform: 'translateY(-50%)',
					width: '10px',
					height: '18px',
					padding: 0,
					background: 'transparent',
					border: 'none',
					...(props.project.canvas?.disabled
						? { cursor: 'not-allowed' }
						: { cursor: 'pointer' }),
					zIndex: 1
				}}
			>
				<svg
					width="10"
					height="18"
					viewBox="0 0 10 18"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					style={{
						display: 'block',
						filter: 'drop-shadow(5px 2px 5px rgba(0, 0, 0, 0.08))'
					}}
				>
					<path
						d="M -2 -2 L 5 7 Q 6.5 9 5 11 L -2 20 Z"
						fill={getCardColorCSSVariable(props.entity)}
						stroke={getCardColorCSSVariable(props.entity)}
						strokeWidth="2"
					/>
				</svg>
			</div>
			{!disabled && !props.parentDisabledReasons.length && (
				<div
					id={`${getCanvasEntityMainValueOutputDOMId(entity)}--new-right-node`}
					className={[
						'canvas__small-node',
						'canvas__small-node--position',
						'canvas-property__node',
						'canvas__node__new',
						'canvas__medium-node'
					].join(' ')}
					ref={canvasSocketTooltip.anchorRef}
					style={{
						top: '50%',
						transform: 'translateY(-50%)'
					}}
					onMouseDown={(e) => {
						e.stopPropagation();

						const permanentNodeElement =
							rootElementRef.current?.querySelector(
								`#${getCanvasEntityMainValueOutputDOMId(entity)}`
							);

						// Add 'canvas__node__active' class to the element
						permanentNodeElement?.classList.add(
							'canvas__node__active'
						);

						// Listen to the mouseup event to remove the class
						const mouseUpListener = (e: MouseEvent) => {
							if (permanentNodeElement) {
								const isConnectedToMenu =
									props.project.onGoingConnectionSource ===
									entity;

								if (!isConnectedToMenu) {
									permanentNodeElement.classList.remove(
										'canvas__node__active'
									);
								}
							}

							if (
								props.project.onGoingConnectionSource !== entity
							) {
								props.project.off(
									'canvas-connection-end',
									mouseUpListener
								);
							}
						};

						props.project.on(
							'canvas-connection-end',
							mouseUpListener
						);

						props.project.startConnection({
							sourceId: `${getCanvasEntityMainValueOutputDOMId(entity)}--connected-right-node`,
							draggableObject:
								props.canvasElement.draggableObject,
							source: entity,
							nodeType: ConnectionNodeType.PropertyOut
						});
					}}
					onMouseUp={(e) => {
						// Remove active class from connected/permanent node
					}}
				/>
			)}
		</li>
	);
}
